import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../App';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  FileText,
  FileSpreadsheet,
  Filter,
  ArrowUpDown,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { format, differenceInDays, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function ContractsReportPage() {
  const { API } = useAuth();
  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState([]);
  const [filteredContracts, setFilteredContracts] = useState([]);
  
  // Filters
  const [tipoRelatorio, setTipoRelatorio] = useState('vigentes');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [ordenarPor, setOrdenarPor] = useState('data_final');
  const [ordem, setOrdem] = useState('crescente');

  useEffect(() => {
    loadContracts();
  }, []);

  useEffect(() => {
    filterAndSortContracts();
  }, [contracts, tipoRelatorio, dataInicio, dataFim, ordenarPor, ordem]);

  const loadContracts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/contracts`, { withCredentials: true });
      setContracts(response.data);
    } catch (error) {
      toast.error('Erro ao carregar contratos');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortContracts = () => {
    let result = [...contracts];

    // Filter by type
    if (tipoRelatorio === 'vigentes') {
      result = result.filter(c => c.status === 'ativo');
    } else if (tipoRelatorio === 'encerrados') {
      result = result.filter(c => c.status === 'encerrado' || c.status === 'cancelado');
    }

    // Filter by date range
    if (dataInicio) {
      result = result.filter(c => c.data_inicio >= dataInicio);
    }
    if (dataFim) {
      result = result.filter(c => c.data_final <= dataFim);
    }

    // Sort
    result.sort((a, b) => {
      let comparison = 0;
      if (ordenarPor === 'data_final') {
        comparison = new Date(a.data_final) - new Date(b.data_final);
      } else if (ordenarPor === 'data_inicio') {
        comparison = new Date(a.data_inicio) - new Date(b.data_inicio);
      } else if (ordenarPor === 'cliente') {
        comparison = (a.client_name || '').localeCompare(b.client_name || '');
      } else if (ordenarPor === 'veiculo') {
        comparison = (a.vehicle_id || '').localeCompare(b.vehicle_id || '');
      }
      return ordem === 'crescente' ? comparison : -comparison;
    });

    setFilteredContracts(result);
  };

  const clearFilters = () => {
    setTipoRelatorio('vigentes');
    setDataInicio('');
    setDataFim('');
    setOrdenarPor('data_final');
    setOrdem('crescente');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'dd/MM/yyyy', { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const getDaysUntilExpiry = (dataFinal) => {
    if (!dataFinal) return null;
    try {
      return differenceInDays(parseISO(dataFinal), new Date());
    } catch {
      return null;
    }
  };

  const getExpiryStatus = (dataFinal) => {
    const days = getDaysUntilExpiry(dataFinal);
    if (days === null) return null;
    
    if (days < 0) {
      return { label: 'Vencido', color: 'bg-red-100 text-red-700', icon: AlertTriangle };
    } else if (days <= 15) {
      return { label: `${days}d`, color: 'bg-yellow-100 text-yellow-700', icon: AlertTriangle };
    } else {
      return { label: 'OK', color: 'bg-green-100 text-green-700', icon: CheckCircle };
    }
  };

  const exportToExcel = () => {
    // Create CSV content
    const headers = ['Veículo', 'Cliente', 'Início', 'Término', 'Status'];
    const rows = filteredContracts.map(c => [
      c.vehicle_id,
      c.client_name,
      formatDate(c.data_inicio),
      formatDate(c.data_final),
      c.status
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(r => r.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `contratos_${tipoRelatorio}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    toast.success('Relatório exportado!');
  };

  const exportToPDF = async () => {
    toast.info('Gerando PDF...');
    // For now, use browser print
    window.print();
  };

  return (
    <div className="space-y-6" data-testid="contracts-report-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800 font-[Manrope]">Relatório de Contratos</h1>
        <p className="text-gray-500 text-sm">Visualize e exporte relatórios de contratos</p>
      </div>

      {/* Filters Card */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-gray-500" />
            <span className="font-medium text-gray-700">Filtros do Relatório</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
            <div>
              <Label className="text-xs text-gray-500">Tipo de Relatório</Label>
              <Select value={tipoRelatorio} onValueChange={setTipoRelatorio}>
                <SelectTrigger data-testid="tipo-contrato-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vigentes">Contratos Vigentes</SelectItem>
                  <SelectItem value="encerrados">Contratos Encerrados</SelectItem>
                  <SelectItem value="todos">Todos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs text-gray-500">Data Início (Filtro)</Label>
              <Input
                type="date"
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                data-testid="data-inicio-filter"
              />
            </div>
            
            <div>
              <Label className="text-xs text-gray-500">Data Fim (Filtro)</Label>
              <Input
                type="date"
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
                data-testid="data-fim-filter"
              />
            </div>
            
            <div>
              <Label className="text-xs text-gray-500">Ordenar Por</Label>
              <Select value={ordenarPor} onValueChange={setOrdenarPor}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="data_final">Data de Término</SelectItem>
                  <SelectItem value="data_inicio">Data de Início</SelectItem>
                  <SelectItem value="cliente">Cliente</SelectItem>
                  <SelectItem value="veiculo">Veículo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs text-gray-500">Ordem</Label>
              <div className="flex gap-1">
                <Button
                  variant={ordem === 'crescente' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setOrdem('crescente')}
                  className="flex-1"
                >
                  ↑ Recentes
                </Button>
                <Button
                  variant={ordem === 'decrescente' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setOrdem('decrescente')}
                  className="flex-1"
                >
                  ↓ Distantes
                </Button>
              </div>
            </div>
            
            <div>
              <Button variant="outline" onClick={clearFilters} className="w-full">
                Limpar Filtros
              </Button>
            </div>
          </div>
          
          {/* Export Buttons */}
          <div className="flex gap-2 mt-4">
            <Button variant="outline" size="sm" onClick={exportToExcel}>
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Exportar Excel
            </Button>
            <Button variant="outline" size="sm" onClick={exportToPDF}>
              <FileText className="w-4 h-4 mr-2" />
              Exportar PDF
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Report Content */}
      <Card className="print:shadow-none">
        <CardContent className="p-6">
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold">
              {tipoRelatorio === 'vigentes' ? 'Contratos Vigentes' : 
               tipoRelatorio === 'encerrados' ? 'Contratos Encerrados' : 'Todos os Contratos'} - NB Locações
            </h2>
            <p className="text-sm text-gray-500">
              Gerado em: {format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
            </p>
            <p className="text-sm text-gray-500">
              Total de registros: {filteredContracts.length}
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredContracts.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhum contrato encontrado</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">VEÍCULO</TableHead>
                    <TableHead className="font-semibold">CLIENTE</TableHead>
                    <TableHead className="font-semibold text-center">INÍCIO</TableHead>
                    <TableHead className="font-semibold text-center">TÉRMINO</TableHead>
                    <TableHead className="font-semibold text-center">STATUS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredContracts.map((contract) => {
                    const expiryStatus = contract.status === 'ativo' ? getExpiryStatus(contract.data_final) : null;
                    // Use vehicle_id directly - it should be the readable ID like "01 pop"
                    const displayVehicleId = contract.vehicle_id;
                    return (
                      <TableRow key={contract.contract_id} className="hover:bg-gray-50">
                        <TableCell className="font-medium">{displayVehicleId}</TableCell>
                        <TableCell>{contract.client_name}</TableCell>
                        <TableCell className="text-center">{formatDate(contract.data_inicio)}</TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-2">
                            {formatDate(contract.data_final)}
                            {expiryStatus && expiryStatus.label !== 'OK' && (
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${expiryStatus.color}`}>
                                {expiryStatus.label}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            contract.status === 'ativo' ? 'bg-green-100 text-green-700' :
                            contract.status === 'encerrado' ? 'bg-gray-100 text-gray-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {contract.status === 'ativo' ? 'ATIVO' : 
                             contract.status === 'encerrado' ? 'ENCERRADO' : 
                             contract.status?.toUpperCase()}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
